import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  LoadingComponent,
  LoadingVariant,
  SignJourneyState
} from '../components/loading/loading.component';

@Injectable({
  providedIn: 'root'
})
export class UiHelperService {
  private loadingDialogRef: MatDialogRef<LoadingComponent> | null = null;
  /** Evita que strip del hide borre un spinner reabierto en el mismo tick. */
  private stripTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private dialog: MatDialog) {}

  /**
   * Overlay de carga unificado.
   * @param mensaje Texto debajo del spinner (detalle).
   * @param variant Contexto visual: sign | verify | admin | auth | download | gestion | generic.
   * @param titulo Título del header (opcional; si no, se deduce del variant/mensaje).
   */
  showSpinner(mensaje: string = 'Procesando...', variant?: LoadingVariant, titulo?: string): void {
    this.cancelScheduledStrip();

    if (this.loadingDialogRef) {
      this.updateSpinnerMessage(mensaje, variant, titulo);
      return;
    }

    // Cierra instancias huérfanas de LoadingComponent (ref perdido por carrera hide→show).
    this.closeStaleLoadingDialogs();

    document.documentElement.classList.add('loading-open');
    document.body.classList.add('loading-open');

    const ref = this.dialog.open(LoadingComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'loading-dialog',
      disableClose: true,
      hasBackdrop: true,
      backdropClass: 'loading-backdrop',
      autoFocus: false,
      restoreFocus: false
    });

    this.loadingDialogRef = ref;

    ref.afterClosed().subscribe(() => {
      // Solo limpiar si este cierre corresponde al spinner activo.
      // Si ya se abrió otro (hide → show inmediato), no tocar su ref ni el DOM.
      if (this.loadingDialogRef === ref) {
        this.loadingDialogRef = null;
      }
      if (!this.loadingDialogRef) {
        this.stripLoadingOverlay();
      }
    });

    if (ref.componentInstance) {
      ref.componentInstance.applyMensaje(mensaje, variant, titulo);
    }
  }

  updateSpinnerMessage(mensaje: string, variant?: LoadingVariant, titulo?: string): void {
    if (!this.loadingDialogRef?.componentInstance) {
      this.showSpinner(mensaje, variant, titulo);
      return;
    }
    this.loadingDialogRef.componentInstance.applyMensaje(mensaje, variant, titulo);
  }

  /**
   * Overlay de firma con viaje del documento (fases Enviar → Firmar → Abrir).
   * Reutiliza el mismo diálogo; solo cambia el contenido visual.
   */
  showSignJourney(state: SignJourneyState & { mensaje?: string }): void {
    const mensaje =
      state.mensaje ||
      (state.fileName
        ? `Firmando ${state.current || 1} de ${state.total || 1}: ${state.fileName}`
        : 'Firmando documentos...');
    this.showSpinner(mensaje, 'sign');
    this.updateSignJourney(state);
  }

  updateSignJourney(state: SignJourneyState): void {
    if (!this.loadingDialogRef?.componentInstance) {
      this.showSignJourney(state);
      return;
    }
    this.loadingDialogRef.componentInstance.applySignJourney(state);
  }

  hideSpinner(): void {
    const ref = this.loadingDialogRef;
    this.loadingDialogRef = null;
    if (ref) {
      try {
        ref.close();
      } catch {
        // ignore
      }
    }
    // Diferir strip: permite showSpinner() sincrónico justo después sin borrar el nuevo overlay.
    this.scheduleStripIfIdle();
  }

  /**
   * Quita restos del overlay de carga (pane/backdrop CDK) que pueden quedar
   * si se abre un Swal encima y el diálogo no termina de desmontarse.
   */
  stripLoadingOverlay(): void {
    // No destruir el overlay si hay un spinner activo legítimo.
    if (this.loadingDialogRef) {
      return;
    }

    document.documentElement.classList.remove('loading-open');
    document.body.classList.remove('loading-open');
    document.documentElement.classList.remove('cdk-global-scrollblock');
    document.body.classList.remove('cdk-global-scrollblock');

    document.querySelectorAll('.cdk-overlay-pane.loading-dialog').forEach((el) => el.remove());
    document.querySelectorAll('.cdk-overlay-backdrop.loading-backdrop').forEach((el) => el.remove());
    // No eliminar aquí .rse-auth-dialog: hideSpinner() llama strip y destruía el modal
    // de autenticación RSE mientras el poll seguía (FAILED sin mensaje al usuario).

    // Backdrops huérfanos (sin pane asociado) que siguen capturando clics.
    document.querySelectorAll('.cdk-overlay-container .cdk-overlay-backdrop').forEach((el) => {
      const parent = el.parentElement;
      if (!parent) {
        el.remove();
        return;
      }
      if (!parent.querySelector('.cdk-overlay-pane')) {
        el.remove();
      }
    });
  }

  /** Desbloquea scroll/clics por si quedó un bloqueo CDK tras spinner + Swal. */
  unlockUi(): void {
    this.cancelScheduledStrip();
    const ref = this.loadingDialogRef;
    this.loadingDialogRef = null;
    if (ref) {
      try {
        ref.close();
      } catch {
        // ignore
      }
    }
    this.closeStaleLoadingDialogs();
    this.stripLoadingOverlay();
    this.stripOrphanRseAuthOverlays();

    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.documentElement.style.overflow = '';
    document.documentElement.style.paddingRight = '';

    document.documentElement.classList.remove('cdk-global-scrollblock', 'loading-open');
    document.body.classList.remove('cdk-global-scrollblock', 'loading-open');

    this.restoreShellAriaHidden();
  }

  /**
   * Swal/CDK ponen aria-hidden="true" en app-root (y hermanos) al abrir overlays.
   * Si el contenedor se destruye a mano, el atributo queda y la app deja de recibir clics.
   */
  restoreShellAriaHidden(): void {
    // Modal Material aún abierto: CDK gestiona aria-hidden.
    if (this.dialog.openDialogs.length > 0) {
      return;
    }
    // SweetAlert aún visible en DOM: no tocar.
    if (document.querySelector('.swal2-container .swal2-popup')) {
      return;
    }

    document.querySelectorAll('app-root[aria-hidden="true"], app-app-shell[aria-hidden="true"]').forEach((el) => {
      el.removeAttribute('aria-hidden');
    });

    Array.from(document.body.children).forEach((el) => {
      if (el.getAttribute('aria-hidden') !== 'true') {
        return;
      }
      if (
        el.classList.contains('swal2-container') ||
        el.classList.contains('cdk-overlay-container') ||
        el.tagName === 'SCRIPT' ||
        el.tagName === 'STYLE' ||
        el.tagName === 'LINK'
      ) {
        return;
      }
      el.removeAttribute('aria-hidden');
    });
  }

  /**
   * Limpia panes RSE huérfanos solo si ya no hay un MatDialog RSE abierto.
   * Evita borrar el modal activo mientras el usuario autentica o ve un error.
   */
  private stripOrphanRseAuthOverlays(): void {
    const rseDialogOpen = this.dialog.openDialogs.some(
      (d) => !!d.componentInstance && typeof (d.componentInstance as { beginExternalAuth?: unknown }).beginExternalAuth === 'function'
    );
    if (rseDialogOpen) {
      return;
    }

    document.querySelectorAll('.cdk-overlay-pane.rse-auth-dialog').forEach((el) => el.remove());
    document.querySelectorAll('.cdk-overlay-backdrop.rse-auth-backdrop').forEach((el) => el.remove());
  }

  private scheduleStripIfIdle(): void {
    this.cancelScheduledStrip();
    this.stripTimer = setTimeout(() => {
      this.stripTimer = null;
      if (!this.loadingDialogRef) {
        this.stripLoadingOverlay();
      }
    }, 0);
  }

  private cancelScheduledStrip(): void {
    if (this.stripTimer != null) {
      clearTimeout(this.stripTimer);
      this.stripTimer = null;
    }
  }

  private closeStaleLoadingDialogs(): void {
    for (const open of [...this.dialog.openDialogs]) {
      if (open.componentInstance instanceof LoadingComponent && open !== this.loadingDialogRef) {
        try {
          open.close();
        } catch {
          // ignore
        }
      }
    }
  }
}
